
  import { defineComponent, ref } from 'vue';
  import VChart from 'vue-echarts';
  import type { EChartsOption } from 'echarts';
  import { BarChart } from 'echarts/charts';
  import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent,
  } from 'echarts/components';
  import type { ECElementEvent } from 'echarts/core';
  import { use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';

  import { useAdaptiveGridBottom } from './composables/useAdaptiveGridBottom';
  import { useEChartsBarChart } from './composables/useEChartsBarChart';
  import { COLORS, ECHARTS_BAR_CHART_PROPS, ECHARTS_LEGEND_OPTIONS, ECHARTS_LOADING_OPTIONS } from './constants';
  import EChartsBase from './EChartsBase.vue';
  import EChartsScrollContainer from './EChartsScrollContainer.vue';
  import { TEChartsLoadingOptions } from './types';
  import { createTooltipPositioning, getEChartsGridOptionsByTitle } from './utils';

  const ROW_HEIGHT = 48;
  const CHART_CHROME_HEIGHT = 130;
  const DEFAULT_CATEGORY_LABEL_WIDTH = 280;
  const HORIZONTAL_GRID_BOTTOM = 42;
  const HORIZONTAL_LEGEND_LABEL_GAP = 12;
  const HORIZONTAL_AXIS_LABEL_RESERVE = 20;

  use([GridComponent, LegendComponent, TitleComponent, TooltipComponent, BarChart, CanvasRenderer, DatasetComponent]);

  export default defineComponent({
    name: 'EChartsHorizontalBar',
    components: {
      EChartsCore: VChart,
      EChartsBase,
      EChartsScrollContainer,
    },
    emits: ['clickedBar'],
    props: {
      ...ECHARTS_BAR_CHART_PROPS,
      height: {
        type: Number,
        default: undefined,
      },
      xAxisName: {
        type: String,
        default: '',
      },
      xAxisMaxRatio: {
        type: Number,
        default: undefined,
      },
      categoryLabelWidth: {
        type: Number,
        default: DEFAULT_CATEGORY_LABEL_WIDTH,
      },
      minWidth: {
        type: Number,
      },
      showLegend: {
        type: Boolean,
        default: true,
      },
      showValueAxisLabels: {
        type: Boolean,
        default: true,
      },
    },
    setup(props) {
      const chartRef = ref<InstanceType<typeof VChart> | null>(null);
      const { gridBottom } = useAdaptiveGridBottom({
        chartRef,
        legendBottom: 8,
        legendLabelGap: HORIZONTAL_LEGEND_LABEL_GAP,
        axisLabelReserve: HORIZONTAL_AXIS_LABEL_RESERVE,
        fallbackBottom: HORIZONTAL_GRID_BOTTOM,
      });
      const barChart = useEChartsBarChart(props);
      const tooltipPositioning = createTooltipPositioning();

      return {
        chartRef,
        gridBottom,
        tooltipPositioning,
        ...barChart,
      };
    },
    computed: {
      chartHeight(): number {
        if (this.height != null) return this.height;
        const legendChrome = this.showLegend ? CHART_CHROME_HEIGHT : 48;
        return Math.max(280, this.labels.length * ROW_HEIGHT + legendChrome);
      },
      getChartOption(): EChartsOption {
        const { max: xAxisMax, interval: xAxisInterval } = this.getValueAxisBounds(this.xAxisMaxRatio);

        return {
          tooltip: {
            show: this.showTooltip,
            trigger: 'axis',
            appendTo: this.tooltipPositioning.appendTo,
            position: this.tooltipPositioning.position,
            enterable: false,
            padding: 0,
            extraCssText:
              'max-width:min(420px, calc(100vw - 32px));white-space:normal;word-break:break-word;overflow-wrap:anywhere;box-sizing:border-box;',
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'transparent',
              },
            },
            formatter: this.buildAxisTooltipFormatter,
          },
          dataset: {
            source: this.getDatasetSource,
          },
          color: this.getSeriesColors,
          legend: {
            ...ECHARTS_LEGEND_OPTIONS,
            show: this.showLegend,
            bottom: 8,
            padding: [8, 0, 0, 0],
            icon: this.legendIcon,
            selectedMode: this.legendSelectable,
          },
          grid: {
            ...getEChartsGridOptionsByTitle(this.titleConfig?.text),
            left: 8,
            top: 24,
            right: 56,
            bottom: this.showLegend ? this.gridBottom : 24,
            containLabel: true,
          },
          xAxis: {
            type: 'value',
            name: this.xAxisName || undefined,
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              align: 'right',
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
            },
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              show: this.showValueAxisLabels && this.getHasData,
              margin: 12,
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
              formatter: this.formatAxisLabel,
            },
            splitLine: {
              show: this.showValueAxisLabels,
              lineStyle: {
                color: 'rgba(120, 120, 120, 0.1)',
              },
            },
            min: this.getHasData ? undefined : 0,
            max: this.getHasData ? xAxisMax : 1,
            interval: xAxisInterval,
          },
          yAxis: {
            type: 'category',
            inverse: true,
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              interval: 0,
              hideOverlap: false,
              margin: 8,
              width: this.categoryLabelWidth,
              overflow: 'truncate',
              ellipsis: '...',
              fontSize: 14,
              color: COLORS.lightGrey,
              fontFamily: 'Fira Sans',
              formatter: this.formatCategoryAxisLabel,
            },
          },
          series: this.buildBarSeries(this.stack ? 'inside' : 'right'),
        };
      },
      getLoadingOptions(): TEChartsLoadingOptions {
        return ECHARTS_LOADING_OPTIONS;
      },
    },
    methods: {
      onChartClick(params: ECElementEvent) {
        const index =
          params.componentType === 'series' && typeof params.dataIndex === 'number' ? params.dataIndex : null;
        const seriesIndex = typeof params.seriesIndex === 'number' ? params.seriesIndex : null;
        this.$emit('clickedBar', index, seriesIndex);
      },
    },
  });
